import { useId, useState } from 'react';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import PrintIcon from '@mui/icons-material/Print';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Divider, Menu, MenuItem, Tooltip } from '@mui/material';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportExcel,
  ExportPrint,
  FilterPanelTrigger,
  Toolbar,
  ToolbarButton,
  type GridCsvExportOptions,
  type GridExcelExportOptions,
  type GridPrintExportOptions,
} from '@mui/x-data-grid-premium';
import { useLocalization } from '@/hooks/useLocalization';
import { GridQuickSearch } from '@/components/grids/gridQuickSearch';

interface AccountsDefaultGridToolbarProps {
  csvOptions?: GridCsvExportOptions;
  printOptions?: GridPrintExportOptions;
  excelOptions?: GridExcelExportOptions;
  showQuickFilter?: boolean;
}

/**
 * The toolbar used for the accounts grid wherever the caller doesn't opt
 * into the advisor search/export toolbar (see `accountsGridToolbar.tsx`).
 * It mirrors DataGrid Premium's default toolbar (columns, filters, export,
 * quick filter) plus any buttons this app adds on top of it.
 */
export function AccountsDefaultGridToolbar({
  csvOptions,
  printOptions,
  excelOptions,
  showQuickFilter = true,
}: AccountsDefaultGridToolbarProps) {
  const { t } = useLocalization();
  const buttonId = useId();
  const menuId = useId();
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const isExportMenuOpen = anchorElement !== null;
  const columnsLabel = t('grid.columns', 'Columns');
  const filtersLabel = t('filtersToolbar', 'Filters');
  const exportLabel = t('export', 'Export');
  const printLabel = t('print', 'Print');

  const closeExportMenu = (): void => {
    setAnchorElement(null);
  };

  return (
    <Toolbar>
      <Tooltip title={columnsLabel}>
        <ColumnsPanelTrigger
          render={<ToolbarButton aria-label={columnsLabel} />}
        >
          <ViewColumnIcon fontSize='small' />
        </ColumnsPanelTrigger>
      </Tooltip>

      <Tooltip title={filtersLabel}>
        <FilterPanelTrigger
          render={<ToolbarButton aria-label={filtersLabel} />}
        >
          <FilterListIcon fontSize='small' />
        </FilterPanelTrigger>
      </Tooltip>

      {showQuickFilter && <GridQuickSearch />}

      <Divider
        orientation='vertical'
        flexItem
        sx={{ mx: 0.5, my: 0.5 }}
      />

      <Tooltip
        title={exportLabel}
        disableInteractive={isExportMenuOpen}
      >
        <ToolbarButton
          id={buttonId}
          aria-label={exportLabel}
          aria-controls={isExportMenuOpen ? menuId : undefined}
          aria-haspopup='menu'
          aria-expanded={isExportMenuOpen ? 'true' : undefined}
          onClick={(event) => {
            setAnchorElement(event.currentTarget);
          }}
        >
          <FileDownloadIcon fontSize='small' />
        </ToolbarButton>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={anchorElement}
        open={isExportMenuOpen}
        onClose={closeExportMenu}
        slotProps={{ list: { 'aria-labelledby': buttonId } }}
      >
        <ExportCsv
          render={<MenuItem />}
          options={csvOptions}
          onClick={closeExportMenu}
        >
          {t('grid.downloadAsCsv', 'Download as CSV')}
        </ExportCsv>
        <ExportExcel
          render={<MenuItem />}
          options={excelOptions}
          onClick={closeExportMenu}
        >
          {t('grid.downloadAsExcel', 'Download as Excel')}
        </ExportExcel>
      </Menu>

      <Tooltip title={printLabel}>
        <ExportPrint
          render={<ToolbarButton aria-label={printLabel} />}
          options={printOptions}
        >
          <PrintIcon fontSize='small' />
        </ExportPrint>
      </Tooltip>
    </Toolbar>
  );
}
